import { useEffect, useState } from 'react'

const LOCAL_NETWORK_PERMISSION_NAME = 'local-network-access'

type LocalNetworkPermissionStatus = PermissionStatus | null

/**
 * React hook that determines whether access to the local network is allowed.
 *
 * This hook uses the Permissions API (when available) to query the
 * `local-network-access` permission and returns `true` only when the permission
 * state is `'granted'`.
 *
 * Fallback behavior:
 * - If the environment does not provide `navigator.permissions.query` (e.g. SSR,
 *   older browsers), the hook returns `true`.
 * - If querying the permission throws a `TypeError` (commonly seen when the
 *   permission name is not recognized by the browser), the hook returns `true`.
 * - For other errors, the hook returns `false`.
 *
 * The hook also subscribes to permission state changes (via
 * `PermissionStatus.addEventListener('change', ...)` or `status.onchange`) and
 * updates its return value accordingly.
 *
 * @returns `true` when local network access is allowed (or assumed allowed on
 *          unsupported platforms); otherwise `false`.
 */
export const useLocalNetworkAccessAllowed = (): boolean => {
  const [isAllowed, setIsAllowed] = useState<boolean>(true)

  useEffect((): void | (() => void) => {
    let cancelled = false
    let status: LocalNetworkPermissionStatus = null

    if (
      typeof navigator === 'undefined' ||
      !navigator.permissions ||
      !navigator.permissions.query
    ) {
      setIsAllowed(true)
      return undefined
    }

    const handleChange = () => {
      if (cancelled) return
      setIsAllowed(status?.state === 'granted')
    }

    const queryLocalNetworkPermission = async () => {
      try {
        status = await navigator.permissions.query({
          name: LOCAL_NETWORK_PERMISSION_NAME as PermissionName,
        })

        if (cancelled) return
        setIsAllowed(status.state === 'granted')

        if (typeof status.addEventListener === 'function') {
          status.addEventListener('change', handleChange)
        } else {
          status.onchange = handleChange
        }
      } catch (error) {
        if (cancelled) return

        if (error instanceof TypeError) {
          setIsAllowed(true)
          return
        }

        setIsAllowed(false)
      }
    }

    void queryLocalNetworkPermission()

    return () => {
      cancelled = true

      if (!status) return

      if (typeof status.removeEventListener === 'function') {
        status.removeEventListener('change', handleChange)
      } else if (status.onchange === handleChange) {
        status.onchange = null
      }
    }
  }, [])

  return isAllowed
}
